/**
 * Mobile CRM Layout — Bottom tab navigation (PWA-optimized).
 * Reemplaza AppLayout (Frame + Sidebar) con un layout mobile-first
 * diseñado para agentes de campo: leads, deals, tareas, llamadas, WA.
 */

import { useEffect, useMemo, Suspense } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { PageLoader } from '@/routes/PageLoader';
import { useAuth } from '@smarty/shared';
import { ROUTES } from '@/config/routes.config';

// ─── Tab config ──────────────────────────────────────
interface MobileTab {
  key: string;
  label: string;
  icon: string;
  path: string;
}

const MOBILE_TABS: MobileTab[] = [
  { key: 'home', label: 'Inicio', icon: '🏠', path: '/m/home' },
  { key: 'inbox', label: 'Chats', icon: '💬', path: '/m/inbox' },
  { key: 'leads', label: 'Contactos', icon: '👥', path: '/m/leads' },
  { key: 'tasks', label: 'Tareas', icon: '✅', path: '/m/tasks' },
  { key: 'more', label: 'Más', icon: '☰', path: '/m/more' },
];

export function MobileLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { storeSlug } = useAuth();

  // Body class for mobile-specific CSS
  useEffect(() => {
    document.body.classList.add('sf-mobile');
    // Force viewport meta for mobile
    const meta = document.querySelector('meta[name="viewport"]') as HTMLMetaElement | null;
    if (meta) {
      meta.content = 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover';
    }
    return () => {
      document.body.classList.remove('sf-mobile');
      if (meta) meta.content = 'width=device-width, initial-scale=1';
    };
  }, []);

  const activeTab = useMemo(() => {
    const path = location.pathname;
    if (path.startsWith('/m/inbox') || path.startsWith('/m/chat/')) return 'inbox';
    if (path.startsWith('/m/leads') || path.startsWith('/m/contact')) return 'leads';
    if (path.startsWith('/m/deals') || path.startsWith('/m/deal/')) return 'deals';
    if (path.startsWith('/m/tasks')) return 'tasks';
    if (path.startsWith('/m/more')) return 'more';
    return 'home';
  }, [location.pathname]);

  // Hide bottom bar on detail pages (for full-screen experience)
  const isDetailPage = /^\/m\/(contact|deal|chat)\//.test(location.pathname);

  return (
    <>
      <div className="sf-mobile-shell">
        {/* Main content — scrollable */}
        <main className="sf-mobile-content">
          {/* Suspense de layout: permite lazy-cargar las páginas /m/* sin envolver
              ruta por ruta (mismo patrón que AppLayout). */}
          <Suspense fallback={<PageLoader variant="content" />}>
            <Outlet />
          </Suspense>
        </main>

        {/* Bottom tab bar */}
        {!isDetailPage && (
          <nav className="sf-mobile-tabs" role="tablist">
            {MOBILE_TABS.map((tab) => (
              <button
                key={tab.key}
                role="tab"
                aria-selected={activeTab === tab.key}
                className={`sf-mobile-tab${activeTab === tab.key ? ' sf-mobile-tab--active' : ''}`}
                onClick={() => navigate(tab.path)}
              >
                <span className="sf-mobile-tab__icon">{tab.icon}</span>
                <span className="sf-mobile-tab__label">{tab.label}</span>
              </button>
            ))}
          </nav>
        )}
      </div>

      <style>{`
        /* ─── Mobile Shell ───────────────────────────── */
        .sf-mobile-shell {
          display: flex;
          flex-direction: column;
          height: 100dvh;
          width: 100vw;
          overflow: hidden;
          background: #f6f6f7;
          -webkit-font-smoothing: antialiased;
        }

        .sf-mobile-content {
          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
          -webkit-overflow-scrolling: touch;
          overscroll-behavior-y: contain;
        }

        /* ─── Bottom Tab Bar ─────────────────────────── */
        .sf-mobile-tabs {
          display: flex;
          align-items: stretch;
          background: #fff;
          border-top: 1px solid #e1e3e5;
          padding-bottom: env(safe-area-inset-bottom, 0);
          flex-shrink: 0;
          z-index: 100;
        }

        .sf-mobile-tab {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 2px;
          padding: 8px 4px 6px;
          border: none;
          background: transparent;
          cursor: pointer;
          color: #6d7175;
          transition: color 0.15s;
          -webkit-tap-highlight-color: transparent;
          position: relative;
        }

        .sf-mobile-tab--active {
          color: #2c6ecb;
        }

        .sf-mobile-tab--active::before {
          content: '';
          position: absolute;
          top: 0;
          left: 25%;
          right: 25%;
          height: 2px;
          background: #2c6ecb;
          border-radius: 0 0 2px 2px;
        }

        .sf-mobile-tab__icon {
          font-size: 20px;
          line-height: 1;
        }

        .sf-mobile-tab__label {
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.02em;
          line-height: 1;
        }

        /* ─── Global mobile overrides ────────────────── */
        body.sf-mobile {
          overscroll-behavior: none;
          -webkit-text-size-adjust: 100%;
        }

        body.sf-mobile .Polaris-Frame,
        body.sf-mobile .Polaris-Frame__Navigation,
        body.sf-mobile .Polaris-Frame__TopBar {
          display: none !important;
        }

        /* ─── Pull-to-refresh visual hint ─────────────── */
        .sf-mobile-ptr {
          text-align: center;
          padding: 12px;
          color: #8c9196;
          font-size: 13px;
        }

        /* ─── Mobile page common styles ──────────────── */
        .sf-m-page {
          padding: 16px;
          padding-top: calc(env(safe-area-inset-top, 0px) + 16px);
        }

        .sf-m-page__header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 16px;
        }

        .sf-m-page__title {
          font-size: 22px;
          font-weight: 700;
          color: #202223;
          margin: 0;
        }

        .sf-m-page__subtitle {
          font-size: 13px;
          color: #6d7175;
          margin: 2px 0 0 0;
        }

        /* ─── Mobile card ────────────────────────────── */
        .sf-m-card {
          background: #fff;
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.08);
        }

        .sf-m-card--flush {
          padding: 0;
        }

        /* ─── Mobile list item ───────────────────────── */
        .sf-m-list-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          border-bottom: 1px solid #f1f2f3;
          cursor: pointer;
          transition: background 0.1s;
          -webkit-tap-highlight-color: rgba(0,0,0,0.04);
        }

        .sf-m-list-item:active {
          background: #f6f6f7;
        }

        .sf-m-list-item:last-child {
          border-bottom: none;
        }

        .sf-m-list-item__avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #e4e5e7;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          font-weight: 600;
          color: #6d7175;
          flex-shrink: 0;
        }

        .sf-m-list-item__body {
          flex: 1;
          min-width: 0;
        }

        .sf-m-list-item__name {
          font-size: 15px;
          font-weight: 600;
          color: #202223;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .sf-m-list-item__meta {
          font-size: 13px;
          color: #6d7175;
          margin-top: 1px;
        }

        .sf-m-list-item__right {
          text-align: right;
          flex-shrink: 0;
        }

        /* ─── Mobile badge ───────────────────────────── */
        .sf-m-badge {
          display: inline-flex;
          align-items: center;
          padding: 2px 8px;
          border-radius: 10px;
          font-size: 11px;
          font-weight: 600;
          line-height: 1.4;
        }

        .sf-m-badge--success { background: #aee9d1; color: #1a5735; }
        .sf-m-badge--warning { background: #ffea8a; color: #594801; }
        .sf-m-badge--info    { background: #a4e8f2; color: #0e4f5c; }
        .sf-m-badge--critical { background: #fed3d1; color: #6b1414; }
        .sf-m-badge--new     { background: #e4e5e7; color: #44474a; }

        /* ─── Mobile quick actions ────────────────────── */
        .sf-m-actions {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .sf-m-action-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 10px 16px;
          border-radius: 10px;
          border: 1px solid #c9cccf;
          background: #fff;
          color: #202223;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          -webkit-tap-highlight-color: transparent;
          transition: background 0.1s;
        }

        .sf-m-action-btn:active {
          background: #f1f2f3;
        }

        .sf-m-action-btn--primary {
          background: #2c6ecb;
          color: #fff;
          border-color: #2c6ecb;
        }

        .sf-m-action-btn--primary:active {
          background: #1f5199;
        }

        .sf-m-action-btn--success {
          background: #008060;
          color: #fff;
          border-color: #008060;
        }

        /* ─── Mobile KPI grid ────────────────────────── */
        .sf-m-kpi-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
        }

        .sf-m-kpi {
          background: #fff;
          border-radius: 12px;
          padding: 14px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.08);
        }

        .sf-m-kpi__label {
          font-size: 12px;
          color: #6d7175;
          margin-bottom: 4px;
        }

        .sf-m-kpi__value {
          font-size: 22px;
          font-weight: 700;
          color: #202223;
        }

        .sf-m-kpi__trend {
          font-size: 12px;
          margin-top: 2px;
        }

        .sf-m-kpi__trend--up { color: #008060; }
        .sf-m-kpi__trend--down { color: #d72c0d; }

        /* ─── Search bar ────────────────────────────── */
        .sf-m-search {
          position: relative;
          margin-bottom: 12px;
        }

        .sf-m-search__input {
          width: 100%;
          padding: 10px 12px 10px 36px;
          border: 1px solid #c9cccf;
          border-radius: 10px;
          font-size: 15px;
          background: #fff;
          outline: none;
          color: #202223;
          -webkit-appearance: none;
        }

        .sf-m-search__input:focus {
          border-color: #2c6ecb;
          box-shadow: 0 0 0 2px rgba(44,110,203,0.25);
        }

        .sf-m-search__icon {
          position: absolute;
          left: 10px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 16px;
          color: #8c9196;
        }

        /* ─── Skeleton loaders ───────────────────────── */
        .sf-m-skeleton {
          background: linear-gradient(90deg, #e4e5e7 25%, #f1f2f3 50%, #e4e5e7 75%);
          background-size: 200% 100%;
          animation: sf-m-shimmer 1.5s infinite;
          border-radius: 6px;
        }

        @keyframes sf-m-shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        /* ─── Detail page header ─────────────────────── */
        .sf-m-detail-header {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          padding-top: calc(env(safe-area-inset-top, 0px) + 12px);
          background: #fff;
          border-bottom: 1px solid #e1e3e5;
          position: sticky;
          top: 0;
          z-index: 50;
        }

        .sf-m-detail-header__back {
          font-size: 22px;
          background: none;
          border: none;
          padding: 4px;
          cursor: pointer;
          color: #2c6ecb;
          -webkit-tap-highlight-color: transparent;
        }

        .sf-m-detail-header__title {
          font-size: 17px;
          font-weight: 600;
          color: #202223;
          flex: 1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* ─── Floating action button ─────────────────── */
        .sf-m-fab {
          position: fixed;
          bottom: calc(70px + env(safe-area-inset-bottom, 0px));
          right: 16px;
          width: 56px;
          height: 56px;
          border-radius: 28px;
          background: #2c6ecb;
          color: #fff;
          border: none;
          font-size: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px rgba(44,110,203,0.4);
          cursor: pointer;
          z-index: 90;
          -webkit-tap-highlight-color: transparent;
        }

        .sf-m-fab:active {
          transform: scale(0.92);
        }

        /* ─── Empty state ────────────────────────────── */
        .sf-m-empty {
          text-align: center;
          padding: 48px 24px;
          color: #6d7175;
        }

        .sf-m-empty__icon {
          font-size: 48px;
          margin-bottom: 12px;
        }

        .sf-m-empty__text {
          font-size: 15px;
        }

        /* ─── Pill filter row ────────────────────────── */
        .sf-m-pills {
          display: flex;
          gap: 8px;
          overflow-x: auto;
          padding: 0 0 12px 0;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
        }

        .sf-m-pills::-webkit-scrollbar { display: none; }

        .sf-m-pill {
          flex-shrink: 0;
          padding: 6px 14px;
          border-radius: 20px;
          border: 1px solid #c9cccf;
          background: #fff;
          font-size: 13px;
          font-weight: 500;
          color: #44474a;
          cursor: pointer;
          -webkit-tap-highlight-color: transparent;
          white-space: nowrap;
        }

        .sf-m-pill--active {
          background: #2c6ecb;
          color: #fff;
          border-color: #2c6ecb;
        }

        /* ─── Section title ──────────────────────────── */
        .sf-m-section-title {
          font-size: 13px;
          font-weight: 600;
          color: #6d7175;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          margin: 16px 0 8px 0;
        }
      `}</style>
    </>
  );
}
